import React from 'react';
import { Clock, BookOpen, Check, Play, ShoppingCart, ArrowRight, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { openHotmart } from './coursesConstants';

export const ModuleCard = ({ course, fmt, currency, moduleCheckout, navigate }) => {
  const offer = moduleCheckout[course.id];
  return (
    <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all duration-300 overflow-hidden flex flex-col">
      <div className="h-40 overflow-hidden relative">
        <img src={course.image} alt={course.title} className="w-full h-full object-cover grayscale-[40%]" onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.pexels.com/photos/3862379/pexels-photo-3862379.jpeg?auto=compress&cs=tinysrgb&w=800&q=80'; }} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute top-4 right-4">
          <Badge className="bg-red-600 text-white">{course.level}</Badge>
        </div>
        {offer ? (
          <div className="absolute bottom-0 left-0 right-0 bg-green-600/95 text-white text-center py-1.5 text-xs font-bold uppercase tracking-wide" data-testid={`module-available-${course.id}`}>
            ⚡ Disponível · Liberação imediata
          </div>
        ) : (
          <div className="absolute bottom-0 left-0 right-0 bg-amber-500/95 text-black text-center py-1.5 text-xs font-bold uppercase tracking-wide" data-testid={`module-soon-${course.id}`}>
            Disponível para venda em breve
          </div>
        )}
      </div>

      <CardHeader className="pb-2">
        <CardTitle className="text-lg text-white">{course.title}</CardTitle>
        <CardDescription className="text-sm text-gray-400 line-clamp-2">
          {course.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between text-sm text-gray-400">
          <div className="flex items-center space-x-2">
            <Clock size={14} className="text-red-500" />
            <span>{course.duration}</span>
          </div>
        </div>

        {course.syllabus && course.syllabus.length > 0 && (
          <div className="bg-black/30 rounded-lg p-3 flex-1">
            <h4 className="text-xs font-semibold text-red-400 mb-2 flex items-center">
              <BookOpen size={12} className="mr-1" />
              Programa Didático
            </h4>
            <div className="space-y-1">
              {course.syllabus.slice(0, 5).map((item, index) => (
                <div key={index} className="flex items-start space-x-2 text-xs text-gray-300">
                  <Check size={10} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-1">{item}</span>
                </div>
              ))}
              {course.syllabus.length > 5 && (
                <div className="text-xs text-gray-500 italic pl-4">
                  +{course.syllabus.length - 5} tópicos...
                </div>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-zinc-700 pt-4 mt-auto space-y-3">
          <div className="text-center">
            <div className="text-2xl font-bold text-white" data-testid={`module-price-${course.id}`}>
              {fmt(course.prices?.ead_recorded || 0)}
            </div>
            {currency === 'BRL' && (
              <div className="text-xs text-gray-400">
                ou 12x de {fmt((course.prices?.ead_recorded || 0) / 12)}
              </div>
            )}
            <div className="text-[10px] text-gray-500 mt-1">Pagamento em PayPal ou cartão de crédito</div>
          </div>

          {offer ? (
            <>
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-green-400 bg-green-500/10 border border-green-500/20 rounded-md py-1.5 px-2">
                <Play size={12} className="flex-shrink-0" />
                <span>Acesso liberado imediatamente após a compra</span>
              </div>
              <Button
                onClick={() => openHotmart(offer)}
                className="w-full bg-green-600 hover:bg-green-700 text-white group"
                data-testid={`module-buy-${course.id}`}
              >
                <ShoppingCart className="mr-2" size={16} />
                Comprar Agora
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
              </Button>
            </>
          ) : (
            <>
              <div className="text-[11px] text-amber-400/90 text-center leading-snug bg-amber-500/10 border border-amber-500/20 rounded-md p-2">
                Venda pelo sistema de checkout <strong>em breve</strong>. Por enquanto, a compra pode ser feita por <strong>solicitação direta no WhatsApp</strong> — enviaremos o link de pagamento do curso escolhido.
              </div>
              <a
                href={`https://wa.me/5561995792667?text=${encodeURIComponent(`Olá! Tenho interesse no módulo "${course.title}" (${fmt(course.prices?.ead_recorded || 0)}). Poderia me enviar o link de pagamento?`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full bg-green-600 hover:bg-green-700 text-white rounded-md py-2 text-sm font-semibold transition-colors"
                data-testid={`module-whatsapp-${course.id}`}
              >
                <MessageSquare className="mr-2" size={16} />
                Comprar via WhatsApp (61) 99579-2667
              </a>
            </>
          )}

          <Button
            onClick={() => navigate(`/course/${course.id}`)}
            variant="outline"
            className="w-full border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
            data-testid={`view-course-${course.id}`}
          >
            <BookOpen className="mr-2" size={16} />
            Ver Curso Completo
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ModuleCard;
